/**
 * PatientAddressPage – 訪問住所 (Patient Addresses)
 * List in card (訪問住所一覧).
 * Row 操作: 編集 (navigate to edit page), 削除.
 */

import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppButton, AppSpin } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import useAppModal from '@/hooks/useAppModal';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import {
  usePatientAddressListQuery,
  usePatientAddressDeleteMutation,
} from '@/hooks/main/usePatientAddresses';
import PatientAddressList from '../components/PatientAddressList';
import type { PatientAddress } from '../types';
import { NotFound } from '@/pages/main/system/NotFound';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

const PatientAddressPage: React.FC = () => {
  const { id, tenant } = useParams<{ id: string; tenant?: string }>();
  const navigate = useNavigate();
  const patientId = id ? parseInt(id, 10) : null;

  const { confirm } = useAppModal();

  const { data: patient, isLoading: patientLoading } = useMainPatient(patientId ?? undefined);
  const { data: list, isLoading: listLoading } = usePatientAddressListQuery(patientId);
  const { deleteMutation } = usePatientAddressDeleteMutation();

  const handleNewClick = useCallback(() => {
    navigate(`/${tenant}/patient_addresses/${id}/create`);
  }, [navigate, tenant, id]);

  const handleEditClick = useCallback(
    (address: PatientAddress) => {
      navigate(`/${tenant}/patient_addresses/${id}/edit/${address.id}`);
    },
    [navigate, tenant, id]
  );

  const handleDeleteClick = useCallback(
    (address: PatientAddress) => {
      if (!patientId) return;
      confirm({
        title: '削除確認',
        content: (
          <div>
            <p>{DELETE_CONFIRM_DATA_MESSAGE}</p>
          </div>
        ),
        okText: 'OK',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteMutation({ patientId, addressId: address.id });
        },
      });
    },
    [patientId, deleteMutation, confirm]
  );

  if (patientId === null || isNaN(patientId)) {
    return <NotFound />;
  }

  if (patientLoading && !patient) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <AppSpin />
      </div>
    );
  }

  if (!patient) {
    return <NotFound />;
  }

  return (
    <div className="relative">
      {listLoading && !list && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3 text-gray-700">
            <AppSpin size="large" />
          </div>
        </div>
      )}

      <CollapsibleCard title="訪問住所一覧">
        <PatientPageHeader
          patient={patient}
          patientId={id!}
          tenant={tenant}
          navigate={navigate}
          activeKey="patient-addresses"
          actions={
            <AppButton
              type="primary"
              onClick={handleNewClick}
              icon={<i className="fa fa-file-text-o" />}
            >
              新規登録
            </AppButton>
          }
        />

        <div className="relative overflow-x-auto">
          <PatientAddressList
            list={list ?? []}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            isLoading={false}
          />
        </div>
      </CollapsibleCard>
    </div>
  );
};

export default PatientAddressPage;
